import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";

const AddressEditDialog = ({
  addressFields,
  addressType,
  isFieldDirty,
  updateField,
  onClose,
  onSave,
  open,
}) => {
  const intl = useIntl();

  // LOCAL STATE
  const [draft, setDraft] = useState([]);
  const [dirtyMap, setDirtyMap] = useState({}); // 👈 NEW

  // INIT / RESET ON OPEN OR ADDRESS CHANGE
  useEffect(() => {
    const initialDraft = addressFields || [];

    setDraft(initialDraft);

    // cache initial dirty state per field
    const initialDirty = {};
    initialDraft.forEach(({ field }) => {
      initialDirty[field] = isFieldDirty?.(field) ?? false;
    });

    setDirtyMap(initialDirty);
  }, [addressFields, addressType, isFieldDirty]);

  // HANDLE CHANGE
  const handleChange = (field, value) => {
    setDraft(prev =>
      prev.map(f =>
        f.field === field ? { ...f, value } : f
      )
    );

    // update local dirty cache immediately
    setDirtyMap(prev => ({
      ...prev,
      [field]:
        true // once changed, it's dirty locally
    }));
  };

  // LOCAL DIRTY CHECK (fast + stable)
  const isDirtyLocal =
    Object.values(dirtyMap).some(Boolean);

  // SAVE (commit only changed fields)
  const handleSave = () => {
    const changedFields = draft.filter(f =>
      dirtyMap[f.field]
    );

    changedFields.forEach(({ field, value }) => {
      updateField(addressType, field, value);
    });

    onSave?.(addressType, changedFields);
  };

  if (!draft?.length) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {intl.formatMessage({ id: "btn.edit" })} {addressType}

        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={1.2} sx={{ pt: 1 }}>
          {draft.map(({ field, label, value }) => (
            <TextField
              key={field}
              label={label}
              value={value || ""}
              onChange={(e) => handleChange(field, e.target.value)}
              fullWidth
              size="small"
              sx={{
                ...(dirtyMap[field] && {
                  backgroundColor: "rgba(255, 193, 7, 0.12)",
                  boxShadow: "inset 3px 0 0 #f7bd02",
                }),
              }}
            />
          ))}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {intl.formatMessage({ id: "btn.cancel" })}
        </Button>

        <Button
          variant="contained"
          disabled={!isDirtyLocal}
          onClick={handleSave}
        >
          {intl.formatMessage({ id: "btn.save" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddressEditDialog;